<template>
  <div class="sc-chat">
    <launcher>
      <template v-slot:header>
        <slot name="header"></slot>
      </template>
      <template v-slot:user-avatar="scopedProps">
        <slot name="user-avatar" :user="scopedProps.user" :message="scopedProps.message"></slot>
      </template>
      <template v-slot:launcher>
        <slot name="launcher"></slot>
      </template>
      <template v-slot:pre-chat>
        <slot name="pre-chat"></slot>
      </template>
      <template v-slot:post-chat>
        <slot name="post-chat"></slot>
      </template>
    </launcher>
  </div>
</template>

<script>
import Launcher from "./components/Launcher";
import CloseIcon from "./assets/close-icon-big.png";
import CloseIconLauncher from "./assets/close-icon.png";
import OpenIcon from "./assets/logo-no-bg.svg";
import FileIcon from "./assets/file.svg";
import ChatIcon from "./assets/chat-icon.svg";
import LoadingIcon from "./assets/loading.gif";
import BackIcon from "./assets/back.svg";

const colors = {};
const defaultColors = {
  header: {
    bg: "#4e8cff",
    text: "#ffffff"
  },
  launcher: {
    bg: "#4e8cff"
  },
  messageList: {
    bg: "#ffffff"
  },
  sentMessage: {
    bg: "#4e8cff",
    text: "#ffffff"
  },
  receivedMessage: {
    bg: "#f4f7f9",
    text: "#565867"
  },
  userInput: {
    bg: "#f4f7f9",
    text: "#565867"
  },
  button: {
    bg: "#ffffff",
    text: "#4e8cff",
    border: "#4e8cff"
  },
  messageStatus: {
    bg: "#4e8cff"
  },
  messageTime: {
    text: "#565867"
  }
};

const widget = {};
const defaultWidget = {
  syncState: true,
  isOpen: false,
  screen: "chat",
  scroll: 100
}

const settings = {};
const defaultSettings = {
  showEmoji: true,
  showFile: true,
  showLocation: true,
  showTypingIndicator: true,
  alwaysScrollToBottom: true,
  focusOnOpen: true,
  title: "Hexabot :)",
  titleImageUrl: "https://i.pravatar.cc/300",
  inputDisabled: false, // unsed yet
  placeholder: { en: "Write something...", fr: "Écrivez quelque chose..." },
  menu: [],
  autoFlush: true,
  allowedUploadTypes: ['image/gif', 'image/png', 'image/jpeg'],
  allowedUploadSize: 2500000
};

const chat = {};
const defaultChat = {
  participants: [],
  messagingState: 0, // 0 = sent, 1 = sending, 2 = uploading
  connectionState: 0,
  messages: [],
  suggestions: [],
  watermarks: false,
  newMessage: null,
  newMessagesCount: 0,
  webviewUrl: '',
  // exhangible data
  message: "",
  payload: null,
  file: null,
  // events
  send: null
};

const icons = {};
const defaultIcons = {
  openIcon: OpenIcon,
  closeIcon: CloseIcon,
  closeIconLauncher: CloseIconLauncher,
  fileIcon: FileIcon,
  chatIcon: ChatIcon,
  loadingIcon: LoadingIcon,
  backIcon: BackIcon
};

export default {
  name: "ScChat",
  components: {
    Launcher
  },
  provide() {
    Object.defineProperties(chat, {
      participants: {
        enumerable: true,
        configurable: true,
        get: () => this.Chat.participants,
        set: participants => {
          this.Chat.participants = participants;
        }
      },
      connectionState: {
        /**
         * States
         * 0: Disconnected
         * 1: Want to connect
         * 2: Trying to connect
         * 3: Connected
         */
        enumerable: true,
        configurable: true,
        get: () => this.Chat.connectionState,
        set: connectionState => {
          const intState = parseInt(connectionState);
          const state = intState >= 0 ? (intState <= 3 ? intState : 3) : 0;
          if (state !== this.Chat.connectionState) {
            this.Chat.connectionState = state;
            state === 1 && this.$emit("wantToConnect");
            state === 3 &&
              this.Settings.alwaysScrollToBottom &&
              (this.Widget.scroll = 101);
          }
        }
      },
      messages: {
        enumerable: true,
        configurable: true,
        get: () => Array.prototype.slice.call(this.Chat.messages).sort((a, b) => {
          let aDate = Date.parse(a.createdAt);
          let bDate = Date.parse(b.createdAt);
          return new Date(aDate) - new Date(bDate);
        }),
        set: messages => {
          this.Chat.messages = messages;
        }
      },
      watermarks: {
        enumerable: true,
        configurable: true,
        get: () => this.Settings.showTypingIndicator && this.Chat.watermarks,
        set: watermarks => {
          this.Chat.watermarks = !!watermarks;
        }
      },
      newMessagesCount: {
        enumerable: true,
        configurable: true,
        get: () => this.Chat.newMessagesCount,
        set: newMessagesCount => {
          this.Chat.newMessagesCount =
            parseInt(newMessagesCount) === newMessagesCount
              ? newMessagesCount
              : 0;
        }
      },
      suggestions: {
        enumerable: true,
        configurable: true,
        get: () => this.Chat.suggestions,
        set: suggestions => {
          this.Chat.suggestions = suggestions;
        }
      },
      newMessage: {
        enumerable: true,
        configurable: true,
        set: newMessage => {
          this.Chat.newMessage = newMessage;
          if (newMessage.type === "typing") {
            return this.Chat.watermarks === true;
          }
          this.Chat.watermarks === false;
          
          if (!this.Chat.messages.find((msg) => newMessage.mid === msg.mid)) {
            this.Chat.messages.push(newMessage);
          }
          
          if ("quick_replies" in newMessage.data) {
            this.Chat.suggestions = newMessage.data.quick_replies.map(qr => ({text: qr.title, payload: qr.payload}));
          } else {
            this.Chat.suggestions = [];
          }
          this.Widget.isOpen || this.Chat.newMessagesCount++;
          this.Settings.alwaysScrollToBottom && (this.Widget.scroll = 101); // @hack
          this.Chat.messagingState = 0;
        }
      },
      message: {
        enumerable: true,
        configurable: true,
        get: () => this.Chat.message,
        set: message => {
          this.Chat.message = message;
        }
      },
      payload: {
        enumerable: true,
        configurable: true,
        get: () => this.Chat.payload,
        set: payload => {
          this.Chat.payload = payload;
        }
      },
      file: {
        enumerable: true,
        configurable: true,
        get: () => this.Chat.file,
        set: file => {
          this.Chat.file = file;
        }
      },
      send: {
        enumerable: true,
        configurable: true,
        set: ({ event, source, data}) => {
          this.Chat.messagingState = data.type === 'file' ? 2 : 1;
          this.$emit("send", event, source, data);
        }
      },
      messagingState: {
        enumerable: true,
        configurable: true,
        get: () => this.Chat.messagingState
      },
      webviewUrl: {
        enumerable: true,
        configurable: true,
        set: (url) => {
          if (url) {
            this.Chat.webviewUrl = url;
            this.Widget.screen = 'webview'
          } else {
            this.Widget.screen = 'chat'
          }
        },
        get: () => this.Chat.webviewUrl
      }
    });

    Object.defineProperties(widget, {
      syncState: {
        enumerable: true,
        configurable: true,
        get: () => this.Widget.syncState,
        set: syncState => {
          this.Widget.syncState = !!syncState;
        }
      },
      isOpen: {
        enumerable: true,
        configurable: true,
        get: () => this.Widget.isOpen,
        set: isOpen => {
          this.Widget.isOpen = !!isOpen;
          this.Widget.syncState &&
            this.Widget.isOpen &&
            (this.Chat.newMessagesCount = 0);
          this.$emit("opened");
          this.Widget.syncState &&
            !this.Widget.isOpen &&
            this.$emit("closed");
        }
      },
      screen: {
        enumerable: true,
        configurable: true,
        get: () => this.Widget.screen,
        set: screen => {
          this.Widget.screen = ["prechat", "postchat", "webview"].indexOf(screen) !== -1 ? screen : "chat";
        }
      },
      scroll: {
        enumerable: true,
        configurable: true,
        get: () => this.Widget.scroll,
        set: scroll => {
          this.Widget.scroll = parseInt(scroll) === scroll ? scroll : 0;
          this.Widget.syncState &&
            this.Widget.scroll === 0 &&
            this.$emit("scrollToTop");
        }
      }
    })

    Object.defineProperties(settings, {
      showEmoji: {
        enumerable: true,
        configurable: true,
        get: () => this.Settings.showEmoji,
        set: showEmoji => {
          this.Settings.showEmoji = !!showEmoji;
        }
      },
      showFile: {
        enumerable: true,
        configurable: true,
        get: () => this.Settings.showFile,
        set: showFile => {
          this.Settings.showFile = !!showFile;
        }
      },
      showLocation: {
        enumerable: true,
        configurable: true,
        get: () => this.Settings.showLocation,
        set: showLocation => {
          this.Settings.showLocation = !!showLocation;
        }
      },
      showTypingIndicator: {
        enumerable: true,
        configurable: true,
        get: () => this.Settings.showTypingIndicator,
        set: showTypingIndicator => {
          this.Settings.showTypingIndicator = !!showTypingIndicator;
        }
      },
      alwaysScrollToBottom: {
        enumerable: true,
        configurable: true,
        get: () => this.Settings.alwaysScrollToBottom,
        set: alwaysScrollToBottom => {
          this.Settings.alwaysScrollToBottom = !!alwaysScrollToBottom;
        }
      },
      focusOnOpen: {
        enumerable: true,
        configurable: true,
        get: () => this.Settings.focusOnOpen,
        set: focusOnOpen => {
          this.Settings.focusOnOpen = !!focusOnOpen;
        }
      },
      title: {
        enumerable: true,
        configurable: true,
        get: () => this.Settings.title,
        set: title => {
          this.Settings.title = title;
        }
      },
      titleImageUrl: {
        enumerable: true,
        configurable: true,
        get: () => this.Settings.titleImageUrl,
        set: titleImageUrl => {
          this.Settings.titleImageUrl = titleImageUrl;
        }
      },
      inputDisabled: {
        enumerable: true,
        configurable: true,
        get: () => this.Settings.inputDisabled,
        set: inputDisabled => {
          this.Settings.inputDisabled = !!inputDisabled;
        }
      },
      placeholder: {
        enumerable: true,
        configurable: true,
        get: () =>
          (this.$i18n && this.Settings.placeholder[this.$i18n.locale]) ||
          this.Settings.placeholder.en ||
          this.Settings.placeholder,
        set: placeholder => {
          this.Settings.placeholder = placeholder;
        }
      },
      menu: {
        enumerable: true,
        configurable: true,
        get: () => this.Settings.menu,
        set: menu => {
          this.Settings.menu = menu;
        }
      },
      autoFlush: {
        enumerable: true,
        configurable: true,
        get: () => this.Settings.autoFlush,
        set: autoFlush => {
          this.Settings.autoFlush = autoFlush;
        }
      },
      allowedUploadTypes: {
        enumerable: true,
        configurable: true,
        get: () => this.Settings.allowedUploadTypes,
        set: allowedUploadTypes => {
          this.Settings.allowedUploadTypes = allowedUploadTypes.split(',');
        }
      },
      allowedUploadSize: {
        enumerable: true,
        configurable: true,
        get: () => this.Settings.allowedUploadSize,
        set: allowedUploadSize => {
          this.Settings.allowedUploadSize = parseInt(allowedUploadSize);
        }
      }
    });

    Object.defineProperties(colors, {
      primary: {
        enumerable: true,
        configurable: true,
        set: bg => {
          colors.header = colors.launcher = colors.sentMessage = colors.messageStatus = {
            bg
          };
          colors.button = { text: bg, border: bg };
        }
      },
      secondary: {
        enumerable: true,
        configurable: true,
        set: bg => {
          colors.receivedMessage = colors.userInput = { bg };
        }
      },
      text: {
        enumerable: true,
        configurable: true,
        set: text => {
          colors.header = colors.sentMessage = colors.messageStatus = { text };
          colors.button = { bg: text };
        }
      },
      textSecondary: {
        enumerable: true,
        configurable: true,
        set: text => {
          colors.receivedMessage = colors.userInput = colors.messageStatus = {
            text
          };
        }
      },
      header: {
        enumerable: true,
        configurable: true,
        get: () => this.Colors.header,
        set: header => {
          header.bg && (this.Colors.header.bg = header.bg);
          header.text && (this.Colors.header.text = header.text);
        }
      },
      launcher: {
        enumerable: true,
        configurable: true,
        get: () => this.Colors.launcher,
        set: launcher => {
          launcher.bg && (this.Colors.launcher.bg = launcher.bg);
        }
      },
      messageList: {
        enumerable: true,
        configurable: true,
        get: () => this.Colors.messageList,
        set: messageList => {
          messageList.bg && (this.Colors.messageList.bg = messageList.bg);
        }
      },
      sentMessage: {
        enumerable: true,
        configurable: true,
        get: () => this.Colors.sentMessage,
        set: sentMessage => {
          sentMessage.bg && (this.Colors.sentMessage.bg = sentMessage.bg);
          sentMessage.text && (this.Colors.sentMessage.text = sentMessage.text);
        }
      },
      receivedMessage: {
        enumerable: true,
        configurable: true,
        get: () => this.Colors.receivedMessage,
        set: receivedMessage => {
          receivedMessage.bg &&
            (this.Colors.receivedMessage.bg = receivedMessage.bg);
          receivedMessage.text &&
            (this.Colors.receivedMessage.text = receivedMessage.text);
        }
      },
      userInput: {
        enumerable: true,
        configurable: true,
        get: () => this.Colors.userInput,
        set: userInput => {
          userInput.bg && (this.Colors.userInput.bg = userInput.bg);
          userInput.text && (this.Colors.userInput.text = userInput.text);
        }
      },
      button: {
        enumerable: true,
        configurable: true,
        get: () => this.Colors.button,
        set: button => {
          button.bg && (this.Colors.button.bg = button.bg);
          button.text && (this.Colors.button.text = button.text);
          button.border && (this.Colors.button.border = button.border);
        }
      },
      messageStatus: {
        enumerable: true,
        configurable: true,
        get: () => this.Colors.messageStatus,
        set: messageStatus => {
          messageStatus.bg && (this.Colors.messageStatus.bg = messageStatus.bg);
        }
      },
      messageTime: {
        enumerable: true,
        configurable: true,
        get: () => this.Colors.messageTime,
        set: messageTime => {
          messageTime.text && (this.Colors.messageTime.text = messageTime.text);
        }
      },
      sent: {
        enumerable: true,
        configurable: true,
        get: () => ({
          color: this.Colors.sentMessage.text,
          backgroundColor: this.Colors.sentMessage.bg
        })
      },
      received: {
        enumerable: true,
        configurable: true,
        get: () => ({
          color: this.Colors.receivedMessage.text,
          backgroundColor: this.Colors.receivedMessage.bg
        })
      }
    });

    Object.defineProperties(icons, {
      closeIcon: {
        enumerable: true,
        configurable: true,
        get: () => this.Icons.closeIcon,
        set: closeIcon => {
          this.Icons.closeIcon = closeIcon;
        }
      },
      closeIconLauncher: {
        enumerable: true,
        configurable: true,
        get: () => this.Icons.closeIconLauncher,
        set: closeIconLauncher => {
          this.Icons.closeIconLauncher = closeIconLauncher;
        }
      },
      openIcon: {
        enumerable: true,
        configurable: true,
        get: () => this.Icons.openIcon,
        set: openIcon => {
          this.Icons.openIcon = openIcon;
        }
      },
      fileIcon: {
        enumerable: true,
        configurable: true,
        get: () => this.Icons.fileIcon,
        set: fileIcon => {
          this.Icons.fileIcon = fileIcon;
        }
      },
      chatIcon: {
        enumerable: true,
        configurable: true,
        get: () => this.Icons.chatIcon,
        set: chatIcon => {
          this.Icons.chatIcon = chatIcon;
        }
      },
      loadingIcon: {
        enumerable: true,
        configurable: true,
        get: () => this.Icons.loadingIcon,
        set: loadingIcon => {
          this.Icons.loadingIcon = loadingIcon;
        }
      },
      backIcon: {
        enumerable: true,
        configurable: true,
        get: () => this.Icons.backIcon,
        set: backIcon => {
          this.Icons.backIcon = backIcon;
        }
      }
    });
    return { chat, settings, widget, colors, icons };
  },
  props: {
    chat: {
      type: Object,
      default() {
        return defaultChat;
      }
    },
    widget: {
      type: Object,
      default() {
        return defaultWidget;
      }
    },
    settings: {
      type: Object,
      default() {
        return defaultSettings;
      }
    },
    colors: {
      type: Object,
      default() {
        return defaultColors;
      }
    },
    icons: {
      type: Object,
      default() {
        return defaultIcons;
      }
    }
  },
  data() {
    return {
      Chat: Object.assign(defaultChat, this.chat),
      Widget: Object.assign(defaultWidget, this.widget),
      Settings: Object.assign(defaultSettings, this.settings),
      Colors: Object.assign(defaultColors, this.colors),
      Icons: Object.assign(defaultIcons, this.icons)
    };
  },
  mounted() {
    this.Chat.message = this.chat.message || "";
    this.Chat.payload = this.chat.payload || null;
    this.Chat.file = this.chat.file || null;
    colors.primary = this.colors.primary;
    colors.seconday = this.colors.secondary;
    colors.text = this.colors.text;
    colors.textSecondary = this.colors.textSecondary;
    Object.defineProperty(this.chat, "message", {
      enumerable: true,
      configurable: true,
      get: () => this.Chat.message,
      set: message => {
        this.Chat.message = message;
      }
    });
    Object.defineProperty(this.chat, "payload", {
      enumerable: true,
      configurable: true,
      get: () => this.Chat.payload,
      set: payload => {
        this.Chat.payload = payload;
      }
    });
    Object.defineProperty(this.chat, "file", {
      enumerable: true,
      configurable: true,
      get: () => this.Chat.file,
      set: file => {
        this.Chat.file = file;
      }
    });
    Object.defineProperty(this.chat, "newMessage", {
      enumerable: true,
      configurable: true,
      set: newMessage => {
        chat.newMessage = newMessage;
        this.Chat.messagingState = 0;
      }
    });
  },
  watch: {
    "chat.participants"(current) {
      chat.participants = current;
    },
    "chat.connectionState"(current) {
      chat.connectionState = current;
    },
    "chat.messages"(current) {
      this.Chat.messages = current;
    },
    "chat.watermarks"(current) {
      this.Chat.watermarks = current;
    },
    "chat.newMessagesCount"(current) {
      this.Chat.newMessagesCount = current;
    },
    "chat.webviewUrl"(current) {
      this.Chat.webviewUrl = current;
    },
    /**
     * The next data watchers are disabled
     * Theses props are reflected onMount
     */
    // "chat.message"(current) {
    //   this.Chat.message = current;
    // },
    // "chat.payload"(current) {
    //   this.Chat.payload = current;
    // },
    // "chat.file"(current) {
    //   this.Chat.file = current;
    // },
    "widget.syncState"(current) {
      widget.syncState = current;
    },
    "widget.isOpen"(current) {
      widget.isOpen = current;
    },
    "widget.screen"(current) {
      widget.screen = current;
    },
    "widget.scroll"(current) {
      widget.scroll = current;
    },
    "settings.showEmoji"(current) {
      settings.showEmoji = current;
    },
    "settings.showFile"(current) {
      settings.showFile = current;
    },
    "settings.showLocation"(current) {
      settings.showLocation = current;
    },
    "settings.showTypingIndicator"(current) {
      settings.showTypingIndicator = current;
    },
    "settings.alwaysScrollToBottom"(current) {
      settings.alwaysScrollToBottom = current;
    },
    "settings.focusOnOpen"(current) {
      settings.focusOnOpen = current;
    },
    "settings.title"(current) {
      settings.title = current;
    },
    "settings.titleImageUrl"(current) {
      settings.titleImageUrl = current;
    },
    "settings.inputDisabled"(current) {
      settings.inputDisabled = current;
    },
    "settings.placeholder"(current) {
      settings.placeholder = current;
    },
    "settings.menu"(current) {
      settings.menu = current;
    },
    "settings.autoFlush"(current) {
      settings.autoFlush = current;
    },
    "settings.allowedUploadTypes"(current) {
      settings.allowedUploadTypes = current.split(',');
    },
    "settings.allowedUploadSize"(current) {
      settings.allowedUploadSize = parseInt(current);
    },
    "colors.primary"(current) {
      colors.primary = current;
    },
    "colors.secondary"(current) {
      colors.secondary = current;
    },
    "colors.text"(current) {
      colors.text = current;
    },
    "colors.textSecondary"(current) {
      colors.textSecondary = current;
    },
    "colors.header"(current) {
      colors.header = current;
    },
    "colors.launcher"(current) {
      colors.launcher = current;
    },
    "colors.messageList"(current) {
      colors.messageList = current;
    },
    "colors.sentMessage"(current) {
      colors.sentMessage = current;
    },
    "colors.receivedMessage"(current) {
      colors.receivedMessage = current;
    },
    "colors.userInput"(current) {
      colors.userInput = current;
    },
    "colors.button"(current) {
      colors.button = current;
    },
    "colors.messageStatus"(current) {
      colors.messageStatus = current;
    },
    "colors.messageTime"(current) {
      colors.messageTime = current;
    },
    "icons.closeIcon"(current) {
      icons.closeIcon = current;
    },
    "icons.closeIconLauncher"(current) {
      icons.closeIconLauncher = current;
    },
    "icons.openIcon"(current) {
      icons.openIcon = current;
    },
    "icons.fileIcon"(current) {
      icons.fileIcon = current;
    },
    "icons.chatIcon"(current) {
      icons.chatIcon = current;
    }
  }
};
</script>

<style>
</style>